<div class="export-templates-container">

  <!-- Page Header — title left, switch right -->
  <div class="d-flex align-items-start justify-content-between mb-3">
    <div class="page-header">
      <h1 class="page-title mb-1 mt-0" translate>EXPORT_TEMPLATES</h1>
      <p class="page-subtitle text-muted mb-0" translate>EXPORT_TEMPLATES_SUBTITLE</p>
    </div>

    <div *ngIf="templatesList != null && isActuator" class="pt-1">
      <div class="mode-switch btn-group" role="group">
        <button
          type="button"
          class="btn"
          [class.btn-primary]="!!templateId"
          [class.btn-outline-secondary]="!templateId"
          (click)="templateId = templatesOptions[0]?.id; setTemplate(templatesOptions[0]?.id); showAlias = false"
        >
          <i class="fas fa-file-alt me-1"></i>
          <span translate>TEMPLATES</span>
        </button>
        <button
          type="button"
          class="btn"
          [class.btn-primary]="!templateId"
          [class.btn-outline-secondary]="!!templateId"
          (click)="showAlias = true; templateId = null; currentTab = ''"
        >
          <i class="fas fa-link me-1"></i>
          <span translate>ALIASES_SHORTHANDS</span>
        </button>
      </div>
    </div>
  </div>

  <div *ngIf="templatesList != null; else loading">
    <!-- Alert Message -->
    <div *ngIf="alertMessage" class="alert text-center mb-3" role="alert"
      [ngClass]="{ 'alert-success': alertMessage.type === 'success', 'alert-danger': alertMessage.type === 'danger' }"
      (click)="alertMessage = null"
    >
      <span>{{ alertMessage.message | translate }}</span>
    </div>

    <!-- Action bar: dropdown left, delete+save right -->
    <div class="action-bar d-flex align-items-center justify-content-between mb-3">
      <!-- Template dropdown + add (template mode) -->
      <div class="d-flex align-items-center gap-2">
        <ng-container *ngIf="!!templateId">
          <div class="template-selector">
            <lib-gung-select-option-input
              gungTranslate=""
              [optionsList]="templatesOptions"
              [suppressLabel]="true"
              [(ngModel)]="templateId"
              (ngModelChange)="setTemplate($event)"
              extraHostClass="mb-0"
            >
            </lib-gung-select-option-input>
          </div>
          <button
            *ngIf="enableCreateTemplate"
            class="btn btn-sm btn-outline-secondary"
            (click)="createNewXlsxModal()"
            title="{{ 'CREATE_NEW_XLSX_TEMPLATE' | translate }}"
          >
            <i class="fas fa-plus"></i>
          </button>
        </ng-container>
      </div>

      <!-- Delete + Save -->
      <div class="d-flex align-items-center gap-2">
        <button
          *ngIf="backendVersion3024"
          type="button"
          class="btn btn-outline-secondary"
          (click)="onDelete()"
          [disabled]="loader || !templateId"
          title="{{ 'DELETE' | translate }}"
        >
          <i class="fas fa-trash-alt me-1"></i>
          <span translate>DELETE</span>
        </button>
        <button
          type="button"
          class="btn btn-primary"
          (click)="onSave()"
          [disabled]="loader || (editedTemplatesId.length === 0 && editedAliasId.length === 0)"
          title="{{ 'SAVE' | translate }}"
        >
          <i class="fas fa-save me-1"></i>
          <span translate>SAVE</span>
          <i *ngIf="loader" class="fas fa-spinner fa-spin ms-2"></i>
        </button>
      </div>
    </div>

    <!-- Aliases content -->
    <div *ngIf="isActuator && !templateId">
      <div class="card">
        <div class="card-header d-flex align-items-center gap-3">
          <h5 class="mb-0 mt-0" translate>ALIASES_SHORTHANDS</h5>
          <button
            *ngIf="isActuator && showAlias && featurePimEnabled"
            type="button"
            class="btn btn-primary btn-sm"
            (click)="syncPim($event)"
            translate
          >
            SYNC_ALIAS_EXPRESSIONS_FROM_PIM_FIELD_DEFINITIONS
          </button>
          <div class="search-input-wrapper ms-auto">
            <i class="fas fa-search search-icon"></i>
            <lib-gung-text-input
              class="search-input"
              gungTranslate=""
              [suppressLabel]="true"
              [placeholder]="'SEARCH_IN_COLUMNS'"
              [(ngModel)]="searchString"
              (ngModelChange)="setSearch($event)"
              extraInputClass="pe-4"
            ></lib-gung-text-input>
          </div>
        </div>
        <div class="card-body">
          <table class="table mb-0">
            <thead>
              <tr>
                <th scope="col" class="text-center" id="idAlias"><span translate>ALIAS</span></th>
                <th scope="col" class="text-center" id="idExpression"><span translate>EXPRESSION</span></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let alias of filteredAlias; index as i" [class.row-edited]="editedAliasId.includes(alias.id)">
                <td headers="idAlias" width="50%">
                  <lib-gung-text-input
                    class="w-100"
                    [gungTranslate]="'ALIAS'"
                    [(ngModel)]="alias.id"
                    [renderStatic]="loader ? true : null"
                    [suppressLabel]="true"
                    (change)="unsaved(alias.id)"
                  ></lib-gung-text-input>
                </td>
                <td headers="idExpression" width="50%">
                  <lib-gung-text-input
                    class="w-100"
                    [gungTranslate]="'EXPRESSION'"
                    [(ngModel)]="alias.expression"
                    [renderStatic]="loader ? true : null"
                    [suppressLabel]="true"
                    (change)="unsaved(alias.id)"
                  ></lib-gung-text-input>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="card-body border-top p-3">
          <button
            type="button"
            class="btn btn-outline-primary btn-sm w-100"
            (click)="addRow()"
            [disabled]="loader"
          >
            <i class="fas fa-plus me-1"></i>
            <span translate>ADD_ALIAS</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Templates content -->
    <div *ngIf="templateId">
      <!-- Settings Card -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0 mt-0" translate>TEMPLATE_SETTINGS</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <!-- Template Name -->
            <div class="col-md-6">
              <lib-gung-text-input
                gungTranslate="TEMPLATE_NAME"
                [(ngModel)]="selectedTemplate.name"
                (change)="unsaved(selectedTemplate.id)"
              ></lib-gung-text-input>
            </div>

            <!-- Type -->
            <div class="col-md-6">
              <lib-gung-select-option-input
                gungTranslate="TYPE"
                [optionsList]="typeOptions"
                [(ngModel)]="selectedTemplate.type"
                (selectedOption)="unsaved(selectedTemplate.id)"
              ></lib-gung-select-option-input>
            </div>

            <!-- Group Name -->
            <div class="col-md-6 pb-3">
              <lib-gung-text-input
                gungTranslate="TEMPLATE_GROUP_NAME"
                [(ngModel)]="selectedTemplate.groupName"
                (change)="unsaved(selectedTemplate.id)"
              ></lib-gung-text-input>
            </div>

            <!-- Available to Roles -->
            <div class="col-md-6">
              <lib-gung-input-multi-select
                *ngIf="!!rolesList && selectedTemplate.showXlsxAutomatically"
                class="multi-select-user-groups"
                [optionsList]="rolesList"
                (selectedValues)="setSelectedValues($event)"
                label="AVAILABLE_TO_ROLES"
              >
              </lib-gung-input-multi-select>
            </div>

            <!-- Description -->
            <div class="col-12" *ngIf="backendVersion3024">
              <lib-gung-text-area-input
                gungTranslate="TEMPLATE_DESCRIPTION"
                rowsNumber="2"
                [(ngModel)]="selectedTemplate.description"
                (change)="unsaved(selectedTemplate.id)"
              ></lib-gung-text-area-input>
            </div>

            <!-- Dynamic Template Toggle -->
            <div class="col-12" *ngIf="enableCreateTemplate">
              <div class="dynamic-template-toggle">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center gap-2">
                    <label class="mb-0 fw-medium" translate>IS_DYNAMIC_DOCUMENT_TEMPLATE</label>
                    <i
                      class="fas fa-info-circle text-muted"
                      [ngbTooltip]="'IS_DYNAMIC_DOCUMENT_TEMPLATE_TOOLTIP' | translate"
                      placement="top"
                    ></i>
                  </div>
                  <div class="form-check form-switch">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="dynamicTemplateSwitch"
                      [(ngModel)]="selectedTemplate.showXlsxAutomatically"
                      (change)="unsaved(selectedTemplate.id)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Sync PIM Button -->
            <div class="col-12 text-center" *ngIf="isActuator && showAlias && featurePimEnabled">
              <button type="button" class="btn btn-primary" (click)="syncPim($event)" translate>
                SYNC_ALIAS_EXPRESSIONS_FROM_PIM_FIELD_DEFINITIONS
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Rows/Columns Table Card -->
      <div class="card">
        <div class="card-header d-flex align-items-center gap-3">
          <button
            class="btn btn-sm"
            [class.btn-primary]="currentTab === 'lines' || !currentTab"
            [class.btn-outline-secondary]="currentTab !== 'lines' && currentTab"
            (click)="currentTab = 'lines'"
          >
            <i class="fas fa-list me-1"></i>
            <span translate>LINES</span>
          </button>
          <button
            class="btn btn-sm"
            [class.btn-primary]="currentTab === 'header'"
            [class.btn-outline-secondary]="currentTab !== 'header'"
            (click)="currentTab = 'header'"
          >
            <i class="fas fa-heading me-1"></i>
            <span translate>HEADER</span>
          </button>
          <span class="text-muted small">
            {{ (currentTab === 'header' ? filteredRowsHeaders : filteredRows).length }} {{ 'OF' | translate }} {{ (currentTab === 'header' ? rowsHeaders : rows).length }} {{ 'COLUMNS_ACTIVE' | translate }}
          </span>
          <div class="search-input-wrapper ms-auto">
            <i class="fas fa-search search-icon"></i>
            <lib-gung-text-input
              class="search-input"
              gungTranslate=""
              [suppressLabel]="true"
              [placeholder]="'SEARCH_IN_COLUMNS'"
              [(ngModel)]="searchString"
              (ngModelChange)="setSearch($event)"
              extraInputClass="pe-4"
            ></lib-gung-text-input>
          </div>
        </div>
        <div class="card-body p-0">
          <!-- Lines Tab -->
          <div [hidden]="currentTab === 'header'">
            <div class="table-responsive">
              <table class="table mb-0 rows-table">
                <thead>
                  <tr>
                    <th scope="col" class="text-center col-row-num" id="idRowNum">#</th>
                    <th scope="col" class="text-center col-checkbox" id="idInclude">
                      <span class="text-nowrap">
                        <span translate>INCLUDE</span>
                        <i
                          class="fas fa-question-circle text-muted ms-1"
                          [ngbTooltip]="'INCLUDE_COLUMN_TOOLTIP' | translate"
                          placement="top"
                          container="body"
                        ></i>
                      </span>
                    </th>
                    <th *ngIf="isActuator" scope="col" class="text-center col-checkbox-wide" id="idIgnoreErrors">
                      <span class="text-nowrap">
                        <span translate>IGNORE_ERRORS</span>
                        <i
                          class="fas fa-question-circle text-muted ms-1"
                          [ngbTooltip]="'IGNORE_ERRORS_COLUMN_TOOLTIP' | translate"
                          placement="top"
                          container="body"
                        ></i>
                      </span>
                    </th>
                    <th scope="col" class="text-left col-title" id="idTitle"><span translate>TITLE</span></th>
                    <th scope="col" class="text-left col-expression" id="idExpression"><span translate>EXPRESSION</span></th>
                    <th *ngIf="isActuator" scope="col" class="text-center col-tools" id="idTools"><span translate>TOOLS</span></th>
                    <th scope="col" class="text-center col-drag" id="idMove"></th>
                  </tr>
                </thead>
                <tbody cdkDropList class="table-body" (cdkDropListDropped)="drop($event)" [cdkDropListAutoScrollStep]="20">
                  <tr class="row-item" [hidden]="!filteredRows.includes(row)" [class.row-edited]="editedRows.has(row)" *ngFor="let row of rows; index as i; trackBy: trackByFn" cdkDrag>
                    <td [attr.colspan]="isActuator ? 7 : 5" class="p-0" *cdkDragPlaceholder>
                      <div class="row-placeholder"></div>
                    </td>
                    <td class="text-center align-middle" headers="idRowNum">
                      <span class="row-num-badge" [class.row-num-badge--edited]="editedRows.has(row)">{{ rows.indexOf(row) + 1 }}</span>
                    </td>
                    <td class="text-center align-middle" headers="idInclude">
                      <lib-gung-checkbox-input
                        [suppressLabel]="true"
                        [(ngModel)]="row.active"
                        [disabled]="loader"
                        (ngModelChange)="unsavedRow(row, selectedTemplate.id)"
                        divClass="justify-content-center"
                      ></lib-gung-checkbox-input>
                    </td>
                    <td *ngIf="isActuator" class="text-center align-middle" headers="idIgnoreErrors">
                      <lib-gung-checkbox-input
                        [suppressLabel]="true"
                        [(ngModel)]="row.ignoreErrors"
                        [disabled]="loader"
                        (ngModelChange)="unsavedRow(row, selectedTemplate.id)"
                        divClass="justify-content-center"
                      ></lib-gung-checkbox-input>
                    </td>
                    <td class="align-middle" headers="idTitle">
                      <lib-gung-text-input
                        class="w-100"
                        [gungTranslate]="'TITLE'"
                        [(ngModel)]="row.title"
                        [renderStatic]="loader"
                        [suppressLabel]="true"
                        (change)="unsavedRow(row, selectedTemplate.id)"
                      ></lib-gung-text-input>
                    </td>
                    <td class="align-middle" headers="idExpression">
                      <div class="d-flex align-items-center gap-2">
                        <div *ngIf="aliasList?.length > 0" class="btn-group btn-group-sm flex-shrink-0" role="group">
                          <button
                            type="button"
                            class="btn"
                            [class.btn-primary]="!row['freeTextMode']"
                            [class.btn-outline-secondary]="row['freeTextMode']"
                            (click)="row['freeTextMode'] = false"
                          >{{ 'ALIAS' | translate }}</button>
                          <button
                            type="button"
                            class="btn"
                            [class.btn-primary]="row['freeTextMode']"
                            [class.btn-outline-secondary]="!row['freeTextMode']"
                            (click)="row['freeTextMode'] = true"
                          >{{ 'ENTER_CUSTOM_PATH' | translate }}</button>
                        </div>
                        <div class="flex-grow-1">
                          <div
                            class="expression-input-wrapper"
                            [class.expanded]="row['expressionExpanded']"
                            *ngIf="row['freeTextMode'] || !aliasList"
                          >
                            <input
                              type="text"
                              class="expression-input"
                              [ngClass]="loader ? 'form-control-plaintext' : 'form-control'"
                              [readonly]="loader"
                              [(ngModel)]="row.expression"
                              (change)="unsavedRow(row, selectedTemplate.id)"
                            />
                            <kbd
                              class="expand-toggle-btn"
                              role="button"
                              tabindex="0"
                              (click)="row['expressionExpanded'] = !row['expressionExpanded']"
                              (keydown.enter)="row['expressionExpanded'] = !row['expressionExpanded']"
                              title="{{ (row['expressionExpanded'] ? 'COLLAPSE' : 'EXPAND') | translate }}"
                            >
                              <i class="fas" [class.fa-expand-alt]="!row['expressionExpanded']" [class.fa-compress-alt]="row['expressionExpanded']"></i>
                            </kbd>
                          </div>
                          <lib-template-alias-select
                            *ngIf="!row['freeTextMode'] && aliasList"
                            [optionsLoader]="row.aliasOptionsLoader"
                            [selectedLabel]="getSelectedAliasId(row)"
                            (selectedValue)="onSelectAlias($event, row)"
                          ></lib-template-alias-select>
                        </div>
                      </div>
                    </td>
                    <td *ngIf="isActuator" class="text-center align-middle" headers="idTools">
                      <div class="btn-group btn-group-sm" role="group">
                        <button type="button" class="btn btn-outline-secondary" (click)="clickEdit(i)" [disabled]="loader" title="{{ 'EDIT' | translate }}"><i class="fas fa-edit"></i></button>
                        <button type="button" class="btn btn-outline-secondary" (click)="clickClone(i)" [disabled]="loader" title="{{ 'CLONE' | translate }}"><i class="fas fa-clone"></i></button>
                        <button type="button" class="btn btn-outline-danger" (click)="clickRemove(i)" [disabled]="loader" title="{{ 'DELETE' | translate }}"><i class="fas fa-trash-alt"></i></button>
                      </div>
                    </td>
                    <td class="text-center align-middle drag-handle" headers="idMove" cdkDragHandle [cdkDragHandleDisabled]="rows.length !== filteredRows.length || loader">
                      <i class="fas fa-grip-vertical"></i>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="p-3 border-top" *ngIf="isActuator">
              <button type="button" class="btn btn-outline-primary btn-sm w-100" (click)="addRow()" [disabled]="loader">
                <i class="fas fa-plus me-1"></i>
                <span translate>ADD_ROW</span>
              </button>
            </div>
          </div>

          <!-- Header Tab -->
          <div [hidden]="currentTab !== 'header'">
            <div class="table-responsive">
              <table class="table mb-0 rows-table">
                <thead>
                  <tr>
                    <th scope="col" class="text-center col-row-num" id="idRowNumHeader">#</th>
                    <th scope="col" class="text-center col-checkbox" id="idIncludeHeader">
                      <span class="text-nowrap">
                        <span translate>INCLUDE</span>
                        <i
                          class="fas fa-question-circle text-muted ms-1"
                          [ngbTooltip]="'INCLUDE_COLUMN_TOOLTIP' | translate"
                          placement="top"
                          container="body"
                        ></i>
                      </span>
                    </th>
                    <th *ngIf="isActuator" scope="col" class="text-center col-checkbox-wide" id="idIgnoreErrorsHeader">
                      <span class="text-nowrap">
                        <span translate>IGNORE_ERRORS</span>
                        <i
                          class="fas fa-question-circle text-muted ms-1"
                          [ngbTooltip]="'IGNORE_ERRORS_COLUMN_TOOLTIP' | translate"
                          placement="top"
                          container="body"
                        ></i>
                      </span>
                    </th>
                    <th scope="col" class="text-left col-title" id="idTitleHeader"><span translate>TITLE</span></th>
                    <th scope="col" class="text-left col-expression" id="idExpressionHeader"><span translate>EXPRESSION</span></th>
                    <th *ngIf="isActuator" scope="col" class="text-center col-tools" id="idToolsHeader"><span translate>TOOLS</span></th>
                    <th scope="col" class="text-center col-drag" id="idMoveHeader"></th>
                  </tr>
                </thead>
                <tbody cdkDropList class="table-body" (cdkDropListDropped)="dropHeaders($event)" [cdkDropListAutoScrollStep]="20">
                  <tr class="row-item" [hidden]="!filteredRowsHeaders.includes(row)" [class.row-edited]="editedRowsHeaders.has(row)" *ngFor="let row of rowsHeaders; index as i; trackBy: trackByFn" cdkDrag>
                    <td [attr.colspan]="isActuator ? 7 : 5" class="p-0" *cdkDragPlaceholder>
                      <div class="row-placeholder"></div>
                    </td>
                    <td class="text-center align-middle" headers="idRowNumHeader">
                      <span class="row-num-badge" [class.row-num-badge--edited]="editedRowsHeaders.has(row)">{{ rowsHeaders.indexOf(row) + 1 }}</span>
                    </td>
                    <td class="text-center align-middle" headers="idIncludeHeader">
                      <lib-gung-checkbox-input
                        gungTranslate=""
                        [suppressLabel]="true"
                        [(ngModel)]="row.active"
                        [disabled]="loader"
                        (ngModelChange)="unsavedRow(row, selectedTemplate.id, true)"
                        divClass="justify-content-center"
                      ></lib-gung-checkbox-input>
                    </td>
                    <td *ngIf="isActuator" class="text-center align-middle" headers="idIgnoreErrorsHeader">
                      <lib-gung-checkbox-input
                        gungTranslate=""
                        [suppressLabel]="true"
                        [(ngModel)]="row.ignoreErrors"
                        [disabled]="loader"
                        (ngModelChange)="unsavedRow(row, selectedTemplate.id, true)"
                        divClass="justify-content-center"
                      ></lib-gung-checkbox-input>
                    </td>
                    <td class="align-middle" headers="idTitleHeader">
                      <lib-gung-text-input
                        class="w-100"
                        [gungTranslate]="'TITLE'"
                        [(ngModel)]="row.title"
                        [renderStatic]="isActuator || loader"
                        [suppressLabel]="true"
                        (change)="unsavedRow(row, selectedTemplate.id, true)"
                      ></lib-gung-text-input>
                    </td>
                    <td class="align-middle" headers="idExpressionHeader">
                      <div class="expression-input-wrapper" [class.expanded]="row['expressionExpanded']">
                        <input
                          type="text"
                          class="expression-input"
                          [ngClass]="loader ? 'form-control-plaintext' : 'form-control'"
                          [readonly]="loader"
                          [(ngModel)]="row.expression"
                          (change)="unsavedRow(row, selectedTemplate.id, true)"
                        />
                        <kbd
                          class="expand-toggle-btn"
                          role="button"
                          tabindex="0"
                          (click)="row['expressionExpanded'] = !row['expressionExpanded']"
                          (keydown.enter)="row['expressionExpanded'] = !row['expressionExpanded']"
                          title="{{ (row['expressionExpanded'] ? 'COLLAPSE' : 'EXPAND') | translate }}"
                        >
                          <i class="fas" [class.fa-expand-alt]="!row['expressionExpanded']" [class.fa-compress-alt]="row['expressionExpanded']"></i>
                        </kbd>
                      </div>
                    </td>
                    <td *ngIf="isActuator" class="text-center align-middle" headers="idToolsHeader">
                      <div class="btn-group btn-group-sm" role="group">
                        <button type="button" class="btn btn-outline-secondary" (click)="clickEditHeaders(i)" [disabled]="loader" title="{{ 'EDIT' | translate }}"><i class="fas fa-edit"></i></button>
                        <button type="button" class="btn btn-outline-secondary" (click)="clickCloneHeaders(i)" [disabled]="loader" title="{{ 'CLONE' | translate }}"><i class="fas fa-clone"></i></button>
                        <button type="button" class="btn btn-outline-danger" (click)="clickRemoveHeaders(i)" [disabled]="loader" title="{{ 'DELETE' | translate }}"><i class="fas fa-trash-alt"></i></button>
                      </div>
                    </td>
                    <td class="text-center align-middle drag-handle" headers="idMoveHeader" cdkDragHandle [cdkDragHandleDisabled]="rowsHeaders.length !== filteredRowsHeaders.length || loader">
                      <i class="fas fa-grip-vertical"></i>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="p-3 border-top" *ngIf="isActuator">
              <button type="button" class="btn btn-outline-primary btn-sm w-100" (click)="addRow()" [disabled]="loader">
                <i class="fas fa-plus me-1"></i>
                <span translate>ADD_ROW</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ng-template #loading>
    <div class="text-center py-5">
      <i class="fas fa-spinner fa-spin fa-3x text-primary"></i>
      <p class="mt-3 text-muted" translate>LOADING</p>
    </div>
  </ng-template>
</div>
